import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { NotificationManager } from "../../../notifications/NotificationManager";
import i18n from "../../../language/i18n";
import { useApp } from "../../../context/AppContextProvider";
import { BaseLoadingWithText } from "../../../library";
import { loginWithExternalClientScreenStyles } from "./LoginWithExternalClientScreenStyles";
import { useHandoffLogin } from "../../../hooks/useHandoffLogin";
import { readExternalClientHandoffTokens } from "../../../utilization/ExternalClientHandoffUtilization";
import {
  buildSsoMobileAppHandoff,
  detectPhonePlatform,
  isMobileAppSsoHandoffEnabled,
  MOBILE_APP_HANDOFF_PATH,
  SsoMobileAppHandoff,
} from "../../../utilization/MobileAppHandoffUtilization";
import SsoMobileAppHandoffCard from "../../../components/common/SsoMobileAppHandoffCard";

/**
 * Where an external-CLIENT transfer lands the user it just provisioned.
 *
 * "External client" here means a partner holding API credentials who calls US to provision a user and
 * receives a redirect URL back — BILFEN, OKYANUS, OĞUZKAAN. It is not the same thing as an external
 * *resource* (K12 / SEBIT), where the partner is the identity provider and we validate their token; those
 * have their own routes precisely so the two are not conflated.
 *
 * The token-pair tail is shared with the SEBIT screen via {@link useHandoffLogin}.
 *
 * **Phones go to the mobile app** (behind `isMobileAppSsoHandoffEnabled`): instead of completing the session in a
 * web app the phone cannot use, the pair is handed to the app — on Android through a package-pinned `intent://`
 * (Play Store fallback), on iOS not at all (the store is offered; the installed app gets this URL as a Universal
 * Link). See `buildSsoMobileAppHandoff`.
 */
const LoginWithExternalClientScreen = () => {
  const navigate = useNavigate();
  const { hash, search } = useLocation();
  const { isFullScreen, setIsFullScreen } = useApp();
  const { completeHandoffLogin } = useHandoffLogin();
  const [mobileAppHandoff, setMobileAppHandoff] =
    useState<SsoMobileAppHandoff | null>(null);

  useEffect(() => {
    if (isFullScreen === false) {
      setIsFullScreen(true);
    }
  }, []);

  useEffect(() => {
    /**
     * Previously the "could not read the user" branch was a bare `return` after a `console.log`, so a failed
     * hand-off sat on the spinner forever with no message and no way back. Routing it here means every exit
     * path says something.
     */
    const failToLogin = (messageKey: string) => {
      NotificationManager.error(i18n.t(messageKey));
      navigate("/login");
      // While the phone hand-off is on, App.tsx exempts this route from its phone "device not supported" modal, but
      // it sits outside the router and only re-checks on resize — so without this a phone would reach /login with
      // the modal still off.
      window.dispatchEvent(new Event("resize"));
    };

    const phonePlatform = isMobileAppSsoHandoffEnabled()
      ? detectPhonePlatform(window.navigator.userAgent)
      : null;
    const { accessToken, refreshToken } = readExternalClientHandoffTokens(
      hash,
      search
    );
    if (phonePlatform && accessToken && refreshToken) {
      // Same strip as the web leg: the pair must not stay in history or a Referer, whichever way it is used.
      window.history.replaceState({}, document.title, window.location.pathname);
      const handoff = buildSsoMobileAppHandoff(
        phonePlatform,
        MOBILE_APP_HANDOFF_PATH,
        { accessToken, refreshToken }
      );
      setMobileAppHandoff(handoff);
      if (handoff.appUrl) {
        window.location.href = handoff.appUrl;
      }
      return;
    }

    completeHandoffLogin(hash, search, failToLogin);
  }, []);

  if (mobileAppHandoff) {
    return (
      <div className="rai-root">
        <style>{loginWithExternalClientScreenStyles}</style>
        <SsoMobileAppHandoffCard handoff={mobileAppHandoff} />
      </div>
    );
  }

  return (
    <div className="rai-root">
      <style>{loginWithExternalClientScreenStyles}</style>
      <BaseLoadingWithText
        size="large"
        primaryColor="#55777f"
        secondaryColor="55777f"
      />
    </div>
  );
};

export default LoginWithExternalClientScreen;
